<nav class="sidenav" [class.sidenav--rail]="rail()" [attr.aria-label]="ariaLabel()">
  <div class="sidenav__header">
    <!-- Two wrappers rather than one, both library-owned. A projected element
         keeps its own style encapsulation, so an app cannot reliably hide part
         of it from a rule scoped to the library's DOM — the library has to do
         the swapping. The mark and wordmark are separate slots, so neither
         <ng-content> is duplicated across the two.

         In the rail the mark doubles as the expand control, which saves the
         collapse toggle a row of its own in a column that has none to spare. -->
    <button
      type="button"
      class="sidenav__brand sidenav__brand--rail"
      aria-label="Expand navigation"
      title="Expand navigation"
      [attr.aria-expanded]="!layout.sidenavCollapsed()"
      (click)="layout.toggleSidenav()"
    >
      <span class="sidenav__brand-mark">
        <ng-content select="[uiSidenavBrandMark]" />
      </span>
    </button>

    <a
      class="sidenav__brand sidenav__brand--full"
      [routerLink]="brandRoute()"
      [attr.aria-label]="brandLabel()"
    >
      <span class="sidenav__brand-name">
        <ng-content select="[uiSidenavBrandName]" />
      </span>
    </a>

    @if (layout.isHandset()) {
      <button
        type="button"
        class="sidenav__icon-button"
        aria-label="Close navigation"
        (click)="layout.closeDrawer()"
      >
        <ui-icon name="close" />
      </button>
    } @else if (!rail()) {
      <button
        type="button"
        class="sidenav__icon-button"
        aria-expanded="true"
        aria-label="Collapse navigation"
        title="Collapse navigation"
        (click)="layout.toggleSidenav()"
      >
        <ui-icon name="panel-left" />
      </button>
    }
  </div>

  <div class="sidenav__body">
    @for (section of sections(); track $index; let sectionIndex = $index) {
      @if (section.heading) {
        <p class="sidenav__heading sidenav__fade">{{ section.heading }}</p>
      }
      <ul class="sidenav__list">
        @for (item of section.items; track keyOf(item); let itemIndex = $index) {
          <!-- routerLinkActive here reports whether any descendant link is active,
               which is how a collapsed group still shows as the current section. -->
          <li routerLinkActive #itemActive="routerLinkActive">
            @if (childrenOf(item); as children) {
              <button
                type="button"
                class="sidenav__link sidenav__group-toggle"
                [class.sidenav__link--active]="itemActive.isActive"
                [attr.aria-expanded]="isExpanded(item)"
                [attr.aria-controls]="'nav-group-' + sectionIndex + '-' + itemIndex"
                [attr.title]="rail() ? item.label : null"
                (click)="toggleGroup(item)"
              >
                <ui-icon class="sidenav__link-icon" [name]="item.icon" />
                <span class="sidenav__link-label sidenav__fade">{{ item.label }}</span>
                <ui-icon class="sidenav__chevron sidenav__fade" name="chevron-down" />
              </button>

              <div
                class="sidenav__group"
                [class.sidenav__group--open]="isExpanded(item) && !rail()"
              >
                <ul
                  class="sidenav__sublist"
                  [id]="'nav-group-' + sectionIndex + '-' + itemIndex"
                  [attr.inert]="isExpanded(item) && !rail() ? null : ''"
                >
                  @for (child of children; track keyOf(child)) {
                    <li>
                      <a
                        class="sidenav__link sidenav__link--child"
                        [routerLink]="child.route"
                        routerLinkActive="sidenav__link--active"
                        [routerLinkActiveOptions]="{ exact: child.exact ?? false }"
                        ariaCurrentWhenActive="page"
                      >
                        <ui-icon class="sidenav__link-icon" [name]="child.icon" />
                        <span class="sidenav__link-label">{{ child.label }}</span>
                        @if (child.badge) {
                          <span class="sidenav__badge">{{ child.badge }}</span>
                        }
                      </a>
                    </li>
                  }
                </ul>
              </div>
            } @else {
              <ng-container *ngTemplateOutlet="navLink; context: { $implicit: item }" />
            }
          </li>
        }
      </ul>
    }
  </div>

  <div class="sidenav__footer">
    @if (footerLinks().length) {
      <ul class="sidenav__list">
        @for (item of footerLinks(); track keyOf(item)) {
          <li>
            <ng-container *ngTemplateOutlet="navLink; context: { $implicit: item }" />
          </li>
        }
      </ul>
    }
    <div class="sidenav__footer-slot" [attr.data-rail]="rail()">
      <ng-content select="[uiSidenavFooter]" />
    </div>
  </div>
</nav>

<ng-template #navLink let-item>
  <a
    class="sidenav__link"
    [routerLink]="item.route"
    routerLinkActive="sidenav__link--active"
    [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
    ariaCurrentWhenActive="page"
    [attr.title]="rail() ? item.label : null"
  >
    <ui-icon class="sidenav__link-icon" [name]="item.icon" />
    <span class="sidenav__link-label sidenav__fade">{{ item.label }}</span>
    @if (item.badge) {
      <span class="sidenav__badge sidenav__fade">{{ item.badge }}</span>
    }
  </a>
</ng-template>
